"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NonComplianceClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
const defaultConfig = {
    authEngine: core_auth_1.AuthEngine.UNKNOWN,
    baseURL: process.env.NON_COMPLIANCE_SERVICE_URL,
};
class NonComplianceClient extends base_client_1.ApiClient {
    constructor(config = {}) {
        if (!config.baseURL) {
            commons_logs_1.log.warn("baseURL is not configured");
        }
        if (config.authEngine === core_auth_1.AuthEngine.UNKNOWN) {
            throw new Error("authEngine for NonComplianceClient is not set");
        }
        if (config.authEngine === core_auth_1.AuthEngine.BASIC) {
            throw new Error("BASIC authEngine for NonComplianceClient is not supported");
        }
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            config.tokenRefreshFn = tokenHandler.getTokenFn();
        }
        super(Object.assign(Object.assign({}, defaultConfig), config));
    }
    async createBoxLabelIncident(creationOptions) {
        const url = "v1/box-label-incident";
        try {
            return await this.post(url, creationOptions);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async listBoxLabelIncidents(shippingPlanId) {
        const url = `v1/box-label-incident`;
        try {
            return await this.get(url, { shippingPlanId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getBoxLabelIncident(boxLabelIncidentId) {
        const url = `v1/box-label-incident/${boxLabelIncidentId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getBoxLabelIncidentSummary(shippingPlanId) {
        const url = "/v1/box-label-incident/summary/query";
        try {
            return this.post(url, { shippingPlanId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async updateBoxLabelIncident(boxLabelIncidentId, updateRequest) {
        const url = `v1/box-label-incident/${boxLabelIncidentId}`;
        try {
            return await this.post(url, updateRequest);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async updateBoxLabelIncidentWithBoxInfo(boxLabelIncidentId, boxInformation) {
        const url = `v1/box-label-incident/${boxLabelIncidentId}/box-information`;
        try {
            return await this.post(url, { boxInformation });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async setBoxLabelIncidentDsku(boxLabelIncidentId, dskuBarcodeQty) {
        const url = `v1/box-label-incident/${boxLabelIncidentId}/dsku`;
        try {
            return await this.post(url, dskuBarcodeQty);
        }
        catch (error) {
            commons_logs_1.log.info({ error }, "custom error log");
            this.handleNonComplianceError(error);
        }
    }
    async getAllUnexpectedSkusForWarehousesInDateRangeV2(startDate, endDate, warehouseIds) {
        const url = "v2/non-compliance/unexpectedSku/query/byWarehouseIdAndTimeRange";
        try {
            return await this.post(url, { startDate, endDate, warehouseIds });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async finalizeBoxContentsForSolvingWhenNoBoxInfo(boxLabelIncidentId) {
        const url = `v1/box-label-incident/${boxLabelIncidentId}/contents`;
        try {
            return await this.post(url, {});
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async solveBoxLabelIncident(boxLabelIncidentId, solveParams) {
        const url = `v1/box-label-incident/${boxLabelIncidentId}/solve`;
        try {
            return await this.post(url, solveParams);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSolvedAndScannedBoxLabelIncidents(shippingPlanId) {
        const url = `/v1/box-label-incident/${shippingPlanId}/solved-and-scanned`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSolvedAndScannedBoxLabelIncidentSummary(sellerId, shippingPlanId) {
        const url = `/v1/box-label-incident/${sellerId}/${shippingPlanId}/solved-and-scanned/summary`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async acknowledgeBoxLabelIncident(sellerId, boxLabelIncidentId) {
        const url = `/v1/box-label-incident/${sellerId}/${boxLabelIncidentId}/acknowledge`;
        try {
            return await this.put(url, {});
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async acknowledgeMultipleBoxLabelIncident(sellerId, boxLabelIncidentIds) {
        const url = `/v1/box-label-incident/${sellerId}/acknowledge/multi`;
        try {
            return await this.post(url, {
                boxLabelIncidentIds,
            });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getASNType(asnId) {
        const url = `v1/non-compliance/${asnId}/type`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createNonComplianceCase(nonComplianceCase) {
        const url = "v1/non-compliance/case";
        try {
            return await this.post(url, nonComplianceCase);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async createNonComplianceCasev2(nonComplianceCase) {
        const url = "v2/non-compliance/case";
        try {
            return await this.post(url, nonComplianceCase);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getNonComplianceProductsIds(sellerId) {
        const url = "v1/non-compliance/id";
        try {
            return await this.get(url, { sellerId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnresolvedNonComplianceCount(sellerId) {
        const url = "v1/non-compliance/unresolvedCount";
        try {
            return await this.get(url, { sellerId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getNonComplianceCases(sellerId) {
        const url = "v1/non-compliance/cases";
        try {
            return await this.get(url, { sellerId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getAllUnknownBarcodeNonComplianceCasesForSeller(sellerId) {
        const url = `v1/non-compliance/unknownBarcode/query/bySeller/${sellerId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnknownBarcodeNonComplianceProductById(productId) {
        const url = `v1/non-compliance/unknownBarcode/query/byProductId/${productId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getAllMissingBarcodeNonComplianceCasesForSeller(sellerId) {
        const url = `v1/non-compliance/missingBarcode/query/bySeller/${sellerId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getMissingBarcodeCaseById(caseId) {
        const url = `v1/non-compliance/missingBarcode/query/byId/${caseId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getAllMissingBarcodeNonComplianceCasesForShipment(shipmentId) {
        const url = `v1/non-compliance/missingBarcode/query/byShipment/${shipmentId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async completeMissingBarcodeCase(caseId) {
        const url = `v1/non-compliance/missingBarcode/${caseId}/complete`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async rollbackMissingBarcodeCaseCompletion(caseId) {
        const url = `v1/non-compliance/missingBarcode/${caseId}/rollbackCompletion`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async createUnexpectedSkuCase(unexpectedSkuCase) {
        const url = "v1/non-compliance/unexpectedSku/create";
        try {
            return await this.post(url, unexpectedSkuCase);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async getAllUnexpectedSkuCasesForSeller(sellerId) {
        const url = `v1/non-compliance/unexpectedSku/query/bySeller/${sellerId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getAllUnexpectedSkuCasesForShipment(shipmentId) {
        const url = `v1/non-compliance/unexpectedSku/query/byShipment/${shipmentId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnexpectedSkuCaseById(caseId) {
        const url = `v1/non-compliance/unexpectedSku/query/byId/${caseId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createUnexpectedSkuCaseV2(unexpectedSkuCase) {
        const url = "v2/non-compliance/unexpectedSku/create";
        try {
            return await this.post(url, unexpectedSkuCase);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async getAllUnexpectedCdskuCasesBetweenIds(startId, endId) {
        const url = `v1/non-compliance/unexpectedCdsku/query/betweenIds/${startId}/${endId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnexpectedCdskuCaseById(caseId) {
        const url = `v1/non-compliance/unexpectedCdsku/query/byId/${caseId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getOrCreateUnexpectedCdskuCase(unexpectedCdskuCase) {
        const url = "v1/non-compliance/unexpectedCdsku/create";
        try {
            return await this.post(url, unexpectedCdskuCase);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async getAllUnexpectedSkuCasesForSellerV2(sellerId) {
        const url = `v2/non-compliance/unexpectedSku/query/bySeller/${sellerId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnexpectedSkuCaseByIdV2(caseId) {
        const url = `v2/non-compliance/unexpectedSku/query/byId/${caseId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getNonComplianceCasesByWarehouse(warehouseId) {
        const url = `v1/non-compliance/cases/${warehouseId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markCaseAsCompletedByWarehouse(caseId) {
        const url = `v1/non-compliance/cases/completedByWarehouse/${caseId}`;
        try {
            return await this.put(url, undefined);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async markCaseAsCompletedByWarehousev2(caseId) {
        const url = `v2/non-compliance/cases/completedByWarehouse/${caseId}`;
        try {
            return await this.put(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async rollbackCompletedByWarehouse(caseId) {
        const url = `v1/non-compliance/cases/rollbackCompletedByWarehouse/${caseId}`;
        try {
            return await this.put(url, undefined);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async rollbackCompletedByWarehousev2(caseId) {
        const url = `v2/non-compliance/cases/rollbackCompletedByWarehouse/${caseId}`;
        try {
            return await this.put(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getNonComplianceProduct(productId) {
        const url = `/v1/non-compliance/unknownBarcode/query/byProductId/${productId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async postResolveNonComplianceProduct(productId, request) {
        const url = `v1/non-compliance/${productId}/resolve`;
        try {
            return await this.post(url, request);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async resolveMissingBarcodeCase(caseId, request) {
        const url = `v1/non-compliance/missingBarcode/${caseId}/resolve`;
        try {
            return await this.post(url, request);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markMissingBarcodeNotMyProduct(caseId) {
        const url = `v1/non-compliance/missingBarcode/${caseId}/markNotMyProduct`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markUnknownBarcodeNotMyProduct(nonComplianceProductId) {
        const url = `v1/non-compliance/unknownBarcode/${nonComplianceProductId}/markNotMyProduct`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createDamagedProduct(damagedProduct) {
        const url = `v1/non-compliance/damaged-product`;
        try {
            return await this.post(url, damagedProduct);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async createDamagedProductv2(damagedProduct) {
        const url = `v2/non-compliance/damaged-product`;
        try {
            return await this.post(url, damagedProduct);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async updateDamagedProductV2(damagedProductId, damagedProduct) {
        const url = `v2/non-compliance/damaged-product/${damagedProductId}`;
        try {
            return await this.put(url, damagedProduct);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createDamagedProductOrderPicking(damagedProductOrderPicking) {
        const url = `v1/non-compliance/damaged-product-order-picking`;
        try {
            return await this.post(url, damagedProductOrderPicking);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async createDamagedProductOrderPickingv2(damagedProductOrderPicking) {
        const url = `v2/non-compliance/damaged-product-order-picking`;
        try {
            return await this.post(url, damagedProductOrderPicking);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async reportCompletion(shippingPlanId, dsku, warehouseId) {
        const url = `v1/non-compliance/${shippingPlanId}/${dsku}/${warehouseId}/reportCompletion`;
        try {
            return await this.put(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getReceivingDamagesByShipment(sellerId, shipmentId) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/${shipmentId}`;
        try {
            return await this.get(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createSelfServeReceivingDamage(damageRequest) {
        const url = `v1/non-compliance/damaged-product-self-serve`;
        try {
            return await this.post(url, damageRequest);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createSelfServePickingDamage(damageRequest) {
        const url = `v1/non-compliance/damaged-product-order-picking-self-serve`;
        try {
            return await this.post(url, damageRequest);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamagesBySellerId(sellerId, damageReportedAt) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/reportedAt/${damageReportedAt}`;
        try {
            return await this.get(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamageById(sellerId, damagedProductId) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/id/${damagedProductId}`;
        try {
            return await this.get(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamagesByFault(sellerId, damageFault, damageReportedAt) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/fault/${damageFault}/reportedAt/${damageReportedAt}`;
        try {
            return await this.get(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamagesByType(sellerId, damageType, damageReportedAt) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/type/${damageType}/reportedAt/${damageReportedAt}`;
        try {
            return await this.get(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamagesByDsku(sellerId, dsku) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/dsku/${dsku}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamagesByDskuAndReportedAt(sellerId, dsku, damageReportedAt) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/dsku/${dsku}/reportedAt/${damageReportedAt}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async disputeDamage(sellerId, damagedProductId) {
        const url = `v1/non-compliance/damaged-product/${sellerId}/case/${damagedProductId}/dispute`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markFirstSellerReminderEmailSent(nonComplianceProductIds) {
        const url = `v1/non-compliance/markFirstSellerReminderEmailSent`;
        try {
            return await this.put(url, nonComplianceProductIds);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markCaseInactive(caseId, deletionContext) {
        const url = `v1/non-compliance/${caseId}/inactiveCase`;
        try {
            return await this.post(url, { context: deletionContext });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markMissingBarcodeCaseInactive(caseId, deletionContext) {
        const url = `/v1/non-compliance/missingBarcode/${caseId}/markInactive`;
        try {
            return await this.post(url, { context: deletionContext });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markOtherNonComplianceInactive(caseId, deletionContext) {
        const url = `/v1/non-compliance/submission/${caseId}/markInactive`;
        try {
            return await this.post(url, { context: deletionContext });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async completeOtherNonComplianceCase(caseId) {
        const url = `/v1/non-compliance/submission/${caseId}/complete`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async rollbackOtherNonComplianceCaseCompletion(caseId) {
        const url = `/v1/non-compliance/submission/${caseId}/rollbackCompletion`;
        try {
            return await this.post(url, undefined);
        }
        catch (error) {
            throw this.handleNonComplianceError(error);
        }
    }
    async nonComplianceIncidentScanPackage(incidentId, cdsku, warehouseId) {
        const url = `/v1/non-compliance/incident/package/scan`;
        try {
            return await this.post(url, {
                incidentId,
                cdsku,
                warehouseId,
            });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async nonComplianceIncidentScanBarcode(incidentId, barcode, warehouseId) {
        const url = `/v1/non-compliance/incident/barcode/scan`;
        try {
            return await this.post(url, {
                incidentId,
                barcode,
                warehouseId,
            });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async nonComplianceIncidentSubmission(submissionRequest) {
        const url = `/v1/non-compliance/incident/submission`;
        try {
            return await this.post(url, submissionRequest);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getTicketCenterCasesByWarehouseIdCreatedStatus(warehouseId) {
        const url = `v3/ticket-center/tickets/${warehouseId}/created`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async searchTicketCenterCasesByWarehouseIdCreatedStatus(warehouseId, query) {
        const url = `v4/ticket-center/tickets/${warehouseId}/created?query=${query}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getTicketCenterCasesByWarehouseIdTodoStatus(warehouseId) {
        const url = `v3/ticket-center/tickets/${warehouseId}/todo`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async searchTicketCenterCasesByWarehouseIdTodoStatus(warehouseId, query) {
        const url = `v4/ticket-center/tickets/${warehouseId}/todo?query=${query}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getTicketCenterCasesMetadataByWarehouseIdTodoStatus(warehouseId) {
        const url = `v1/ticket-center/tickets/${warehouseId}/todo/metadata`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getTicketCenterCasesByWarehouseIdClosedStatus(warehouseId) {
        const url = `v3/ticket-center/tickets/${warehouseId}/closed`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async searchTicketCenterCasesByWarehouseIdClosedStatus(warehouseId, query) {
        const url = `v4/ticket-center/tickets/${warehouseId}/closed?query=${query}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSingleTicketCenterCase(type, warehouseId, caseId) {
        const url = `v1/ticket-center/tickets/query/byId/${caseId}`;
        try {
            return await this.get(url, { type, warehouseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createFollowupZendeskTicket(type, warehouseId, caseId, notes) {
        const url = `v1/ticket-center/${caseId}/createFollowup`;
        try {
            return await this.post(url, { type, warehouseId, notes });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getBillingTierSummary(sellerId, timestampInMSec) {
        const url = `v1/billing/tier/${sellerId}/${timestampInMSec}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getChargesForNonCompliance(sourceId, sourceType) {
        const url = `/v1/non-compliance/billing/charges/query`;
        try {
            return await this.post(url, {
                sourceId,
                sourceType,
            });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDamagedProductById(damagedProductId) {
        const url = `/v1/non-compliance/damaged-product/query`;
        try {
            return this.post(url, { damagedProductId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSellerProblems(startId, endId) {
        const url = `/v1/non-compliance/seller-problems/query`;
        try {
            return this.post(url, { startId, endId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getNonComplianceCaseById(caseId) {
        const url = `/v1/non-compliance/case/query`;
        try {
            return this.post(url, { caseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async chargeSellerForNonCompliance(billToCharge) {
        const url = "v1/billing/received";
        try {
            return await this.post(url, billToCharge);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createMissingBoxesAlert(sellerId, shippingPlanId) {
        const url = "v1/non-compliance/alert/missing-boxes";
        try {
            return await this.post(url, {
                sellerId,
                shippingPlanId,
            });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getMissingBoxesAlert(alertId) {
        const url = `v1/non-compliance/alert/missing-boxes/${alertId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async acknowledgeMissingBoxesAlert(alertId) {
        const url = `v1/non-compliance/alert/missing-boxes/${alertId}/acknowledge`;
        try {
            return await this.post(url, {});
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async updateMissingBoxesAlertStatus(alertId, status, details) {
        const url = `/v1/non-compliance/alert/missing-boxes/${alertId}/status/${status}`;
        try {
            return await this.put(url, details);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getMissingBoxesAlertSummary(shippingPlanId) {
        const url = `v1/non-compliance/alert/missing-boxes-summary/${shippingPlanId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getProductsByPlanIds(shippingPlanIds) {
        const query = shippingPlanIds.map((planId) => `shippingPlanId=${planId}`).join("&");
        const url = `v1/non-compliance/products?${query}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getActionNeededShipmentsBySellerId(sellerId) {
        const url = `/v1/non-compliance/actionNeeded/${sellerId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getNoncomplianceShipmentSummary(shippingPlanId, shipmentId, sellerId) {
        const url = `v1/non-compliance/summary/${sellerId}/${shippingPlanId}/${shipmentId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnreceivedNoncomplianceCasesByShipmentId(shipmentId) {
        const url = `v1/non-compliance/cases/unreceived/${shipmentId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async submitOtherFormNonCompliance(otherNonComplianceData) {
        const url = "v2/non-compliance/submission";
        try {
            return await this.post(url, otherNonComplianceData);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createBoxAudit(warehouseId, cdsku) {
        const url = "v1/non-compliance/box-audit";
        try {
            return await this.post(url, { warehouseId, cdsku });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async updateBoxAudit(boxAuditId, items) {
        const url = "v1/non-compliance/box-audit/update";
        try {
            return await this.post(url, { boxAuditId, items });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getBoxAudit(boxAuditId) {
        const url = `v1/non-compliance/box-audit/${boxAuditId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async completeBoxAudit(boxAuditId, action, ejectionReason) {
        const url = `v1/non-compliance/box-audit/complete`;
        try {
            return await this.post(url, { boxAuditId, action, ejectionReason });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async addUnknownBarcodeBoxAuditItem(unknownBarcodeBoxAuditItem) {
        const url = `v1/non-compliance/box-audit/unknown-barcode`;
        try {
            return await this.post(url, unknownBarcodeBoxAuditItem);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getUnknownBarcodeItems(cdsku) {
        const url = `v1/non-compliance/box-audit/unknown-barcodes/${cdsku}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSellerProblemsDetailView(id) {
        const url = `/v1/seller-problems/${id}/detailView`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSellerProblemsBySellerId(sellerId) {
        const url = `/v1/seller-problems/bySellerId/${sellerId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSellerProblemsByShipmentId(shipmentId) {
        const url = `/v1/seller-problems/byShipmentId?shipmentId=${shipmentId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSellerProblemsBySellerIdAndProblemTypes(sellerId, problemTypes) {
        const problemTypesString = problemTypes.join(",");
        const url = `/v1/seller-problems/bySellerIdAndProblemTypes?sellerId=${sellerId}&problemTypes=${problemTypesString}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async searchSellerProblems(request) {
        const url = `/v1/seller-problems/search`;
        try {
            return await this.post(url, request);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getDetailedSellerProblems(shipmentIds, shippingPlanIds) {
        const url = `/v1/seller-problems/detailView`;
        const searchParams = new URLSearchParams();
        if (shipmentIds.length > 0) {
            shipmentIds.forEach((id) => searchParams.append("shipmentIds", id.toString()));
        }
        if (shippingPlanIds.length > 0) {
            shippingPlanIds.forEach((id) => searchParams.append("shippingPlanIds", id.toString()));
        }
        try {
            return await this.get(url + "?" + searchParams);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getSellerProblemsBySellerProblemIdAndType(sellerProblemId, type) {
        const url = `/v1/seller-problems/bySellerProblemIdAndType?sellerProblemId=${sellerProblemId}&type=${type}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async acknowledgeSellerProblemBySellerProblemIdAndType(sellerProblemId, type) {
        const url = `/v1/seller-problems/acknowledgeBySellerProblemIdAndType`;
        try {
            return await this.post(url, { sellerProblemId, type });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async acknowledgeSellerProblemBySellerProblemViewId(sellerProblemViewId) {
        const url = `/v1/seller-problems/acknowledgeBySellerProblemViewId`;
        try {
            return await this.post(url, { sellerProblemViewId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getInvalidLotExpiryCase(caseId) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry/case?caseId=${caseId}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async getInvalidLotExpiryCaseByStatus(invalidLotExpiryCaseStatus) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry/casesByStatus?status=${invalidLotExpiryCaseStatus}`;
        try {
            return await this.get(url);
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markInvalidLotExpiredCaseAsResolved(caseId, resolutionDetails) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry/resolved`;
        try {
            return await this.post(url, { caseId, resolutionDetails });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markInvalidLotExpiredCaseAsCompleted(caseId) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry/completed`;
        try {
            return await this.post(url, { caseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markInvalidLotExpiredCaseAsCancelled(caseId) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry/cancelled`;
        try {
            return await this.post(url, { caseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async rollbackCompletedInvalidLotExpiredCase(caseId) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry/rollbackCompletion`;
        try {
            return await this.post(url, { caseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markDamagedProductAsCompleted(caseId) {
        const url = `/v2/non-compliance/cases/damaged-product/markComplete`;
        try {
            return await this.post(url, { caseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async markDamageProductAsCancelled(caseId, reason) {
        const url = `/v2/non-compliance/cases/damaged-product/markCancelled`;
        try {
            return await this.post(url, { caseId, reason });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async rollbackCompletedDamagedProduct(caseId) {
        const url = `/v2/non-compliance/cases/damaged-product/rollbackCompletion`;
        try {
            return await this.post(url, { caseId });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    async createInvalidLotExpiryCase(invalidLotExpiryCaseRequest, lotNonComplianceSubtype, expiryNonComplianceSubtype) {
        const url = `/v2/non-compliance/cases/invalid-lot-expiry`;
        try {
            return await this.post(url, {
                invalidLotExpiryCaseRequest,
                lotNonComplianceSubtype,
                expiryNonComplianceSubtype,
            });
        }
        catch (error) {
            this.handleNonComplianceError(error);
        }
    }
    handleNonComplianceError(error) {
        throw this.maybeGetDeliverrError(error) || this.maybeGetNetworkError(error) || this.getUnknownError(error);
    }
}
exports.NonComplianceClient = NonComplianceClient;
exports.default = NonComplianceClient;
//# sourceMappingURL=NonComplianceClient.js.map